import React, { useCallback, useMemo, memo, Component } from 'react';
import { NavLink } from 'react-router-dom';
import { safeMap, safeFilter, ensureArray } from '../utils/safeArray';
// Icons
import { BarChart3Icon, BuildingIcon, CheckSquareIcon, FileEditIcon, HomeIcon, ImageIcon, LayersIcon, LayoutGridIcon, LinkIcon, PackageIcon, PlayCircleIcon, SendIcon, SettingsIcon, ShieldCheckIcon, SparklesIcon, TrendingUpIcon, UsersIcon } from 'lucide-react';
// Contexts
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
// Types
type Role = 'agilebrain-admin' | 'client-admin';
interface NavItem {
  path: string;
  icon: ComponentType<{
    className?: string;
  }>;
  label: string;
  allowedRoles: Role[];
  section: 'overview' | 'client-ops' | 'content' | 'admin';
}
// Memoized NavLink component
const MemoizedNavLink = memo(({
  item,
  translation
}: {
  item: NavItem;
  translation: string;
}) => {
  const Icon = item.icon;
  return <NavLink to={item.path} className={({
    isActive
  }) => `group flex items-center px-3 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${isActive ? 'bg-gradient-to-r from-primary to-primary-hover text-white shadow-level-2' : 'text-gray-300 hover:bg-shell-light hover:text-white'}`} data-id="element-269">
        {({
      isActive
    }) => <>
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center mr-3 transition-all duration-200 ${isActive ? 'bg-white/20' : 'bg-shell-light group-hover:bg-shell-light/80'}`} data-id="element-270">
              <Icon className={`w-5 h-5 transition-all duration-200 ${isActive ? 'text-white' : 'text-gray-400 group-hover:text-white'}`} data-id="element-271" />
            </div>
            <span className="flex-1" data-id="element-272">{translation}</span>
            {isActive && <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" data-id="element-273" />}
          </>}
      </NavLink>;
});
MemoizedNavLink.displayName = 'MemoizedNavLink';
/**
 * Navigation Component - Optimized for Performance
 * Uses memo and useMemo to prevent unnecessary re-renders
 */
export const Navigation = memo(() => {
  const authContext = useAuth();
  const orgContext = useOrganization();
  if (!authContext || !orgContext) {
    return null;
  }
  const {
    role
  } = authContext;
  const {
    currentOrganization,
    t
  } = orgContext;
  // Memoize translations with fallback
  const translations = useMemo(() => t || {
    appTitle: 'AgileBrain Hub',
    appSubtitle: 'Loading...',
    sectionOperations: 'Operations',
    sectionContent: 'Content',
    sectionAdministration: 'Administration',
    currentOrganization: 'Current Organization',
    skipToContent: 'Skip to content'
  }, [t]);
  // Memoize navigation items - only recreate when role changes
  const navItems = useMemo<NavItem[]>(() => [
  // Overview Section
  {
    path: '/dashboard',
    icon: HomeIcon,
    label: 'dashboard',
    allowedRoles: ['agilebrain-admin', 'client-admin'],
    section: 'overview'
  },
  // Client Operations Section
  {
    path: '/results',
    icon: BarChart3Icon,
    label: 'results',
    allowedRoles: ['agilebrain-admin', 'client-admin'],
    section: 'client-ops'
  }, {
    path: '/analytics',
    icon: TrendingUpIcon,
    label: 'analytics',
    allowedRoles: ['agilebrain-admin', 'client-admin'],
    section: 'client-ops'
  }, {
    path: '/assignment-management',
    icon: CheckSquareIcon,
    label: 'assignmentManagement',
    allowedRoles: ['agilebrain-admin', 'client-admin'],
    section: 'client-ops'
  }, {
    path: '/participant-management',
    icon: UsersIcon,
    label: 'participantManagement',
    allowedRoles: ['agilebrain-admin', 'client-admin'],
    section: 'client-ops'
  }, {
    path: '/runner-preview',
    icon: PlayCircleIcon,
    label: 'runnerPreview',
    allowedRoles: ['agilebrain-admin', 'client-admin'],
    section: 'client-ops'
  },
  // Content Management Section
  {
    path: '/taxonomy',
    icon: LayoutGridIcon,
    label: 'taxonomy',
    allowedRoles: ['agilebrain-admin', 'client-admin'],
    section: 'content'
  }, {
    path: '/image-library',
    icon: ImageIcon,
    label: 'imageLibrary',
    allowedRoles: ['agilebrain-admin'],
    section: 'content'
  }, {
    path: '/image-sets',
    icon: LayersIcon,
    label: 'imageSets',
    allowedRoles: ['agilebrain-admin'],
    section: 'content'
  }, {
    path: '/supersets',
    icon: PackageIcon,
    label: 'Supersets',
    allowedRoles: ['agilebrain-admin'],
    section: 'content'
  }, {
    path: '/exercise-authoring',
    icon: FileEditIcon,
    label: 'exerciseAuthoring',
    allowedRoles: ['agilebrain-admin'],
    section: 'content'
  }, {
    path: '/publish',
    icon: SendIcon,
    label: 'publishExercise',
    allowedRoles: ['agilebrain-admin'],
    section: 'content'
  }, {
    path: '/manage-exercises',
    icon: SettingsIcon,
    label: 'manageExercises',
    allowedRoles: ['agilebrain-admin'],
    section: 'content'
  },
  // System Administration Section
  {
    path: '/organization-setup',
    icon: BuildingIcon,
    label: 'organizationSetup',
    allowedRoles: ['agilebrain-admin'],
    section: 'admin'
  }, {
    path: '/roles-and-access',
    icon: ShieldCheckIcon,
    label: 'rolesAndAccess',
    allowedRoles: ['agilebrain-admin'],
    section: 'admin'
  }, {
    path: '/integrations',
    icon: LinkIcon,
    label: 'integrations',
    allowedRoles: ['agilebrain-admin', 'client-admin'],
    section: 'admin'
  }], []); // Empty dependency - navItems structure never changes
  // Memoize filtered items by role
  const visibleNavItems = useMemo(() => safeFilter('Navigation.visibleNavItems', navItems, item => {
    const allowedRoles = ensureArray('item.allowedRoles', item.allowedRoles);
    return allowedRoles.includes(role as Role);
  }), [navItems, role]);
  // Memoize grouped items by section
  const groupedItems = useMemo(() => ({
    overview: safeFilter('Navigation.groupedItems.overview', visibleNavItems, item => item.section === 'overview'),
    'client-ops': safeFilter('Navigation.groupedItems.client-ops', visibleNavItems, item => item.section === 'client-ops'),
    content: safeFilter('Navigation.groupedItems.content', visibleNavItems, item => item.section === 'content'),
    admin: safeFilter('Navigation.groupedItems.admin', visibleNavItems, item => item.section === 'admin')
  }), [visibleNavItems]);
  // Memoize section title getter
  const getSectionTitle = useCallback((section: string): string | null => {
    if (section === 'overview') return null;
    if (section === 'client-ops') return translations.sectionOperations;
    if (section === 'content') return translations.sectionContent;
    if (section === 'admin') return translations.sectionAdministration;
    return null;
  }, [translations]);
  return <nav className="fixed left-0 top-0 h-screen w-64 bg-shell flex flex-col shadow-level-3 z-50" data-id="element-274">
      {/* Skip to content link for accessibility */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-white focus:rounded-xl focus:shadow-level-2" data-id="element-275">
        {translations.skipToContent}
      </a>
      {/* Header */}
      <div className="px-6 py-6 border-b border-shell-light/50" data-id="element-276">
        <div className="flex items-center gap-3 mb-2" data-id="element-277">
          <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary-hover rounded-xl flex items-center justify-center shadow-level-1" data-id="element-278">
            <SparklesIcon className="w-6 h-6 text-white" data-id="element-279" />
          </div>
          <h1 className="text-xl font-semibold text-white tracking-tight" data-id="element-280">
            {translations.appTitle}
          </h1>
        </div>
        <p className="text-[10px] text-gray-400 font-medium ml-[52px] tracking-wide" data-id="element-281">
          {translations.appSubtitle}
        </p>
      </div>
      {/* Navigation Items */}
      <div className="flex-1 overflow-y-auto py-6 px-3 custom-scrollbar" data-id="element-282">
        <div className="space-y-6" data-id="element-283">
          {/* Overview Section */}
          {groupedItems.overview.length > 0 && <div className="space-y-1" data-id="element-284">
              {safeMap('Navigation.overview.items', groupedItems.overview, item => <MemoizedNavLink key={item.path} item={item} translation={translations[item.label] || item.label} data-id="element-285" />)}
            </div>}
          {/* Client Operations Section */}
          {groupedItems['client-ops'].length > 0 && <div className="space-y-1" data-id="element-286">
              <div className="px-3 py-2 mb-1" data-id="element-287">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-288">
                  {getSectionTitle('client-ops')}
                </h3>
              </div>
              {safeMap('Navigation.client-ops.items', groupedItems['client-ops'], item => <MemoizedNavLink key={item.path} item={item} translation={translations[item.label] || item.label} data-id="element-289" />)}
            </div>}
          {/* Content Management Section */}
          {groupedItems.content.length > 0 && <div className="space-y-1" data-id="element-290">
              <div className="px-3 py-2 mb-1" data-id="element-291">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-292">
                  {getSectionTitle('content')}
                </h3>
              </div>
              {safeMap('Navigation.content.items', groupedItems.content, item => <MemoizedNavLink key={item.path} item={item} translation={translations[item.label] || item.label} data-id="element-293" />)}
            </div>}
          {/* Administration Section */}
          {groupedItems.admin.length > 0 && <div className="space-y-1" data-id="element-294">
              <div className="px-3 py-2 mb-1" data-id="element-295">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-296">
                  {getSectionTitle('admin')}
                </h3>
              </div>
              {safeMap('Navigation.admin.items', groupedItems.admin, item => <MemoizedNavLink key={item.path} item={item} translation={translations[item.label] || item.label} data-id="element-297" />)}
            </div>}
        </div>
      </div>
      {/* Organization Info Footer */}
      <div className="px-4 py-4 border-t border-shell-light/50 bg-shell-light/30" data-id="element-298">
        <div className="bg-shell-light rounded-xl p-4 border border-shell-light" data-id="element-299">
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2" data-id="element-300">
            {translations.currentOrganization}
          </div>
          <div className="font-semibold text-white text-sm mb-2 truncate" data-id="element-301">
            {currentOrganization?.name || 'Loading...'}
          </div>
          <div className="text-gray-400 text-xs font-mono bg-shell/50 rounded px-2 py-1 inline-block truncate max-w-full" data-id="element-302">
            {currentOrganization?.spacesOrgId || 'org-id'}
          </div>
        </div>
      </div>
    </nav>;
});
Navigation.displayName = 'Navigation';